import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { connect } from "react-redux";
import { Button, Col, DatePicker, Layout, Row, Select, Space, Table, Typography } from "antd";
import { ArrowLeftOutlined, PrinterOutlined } from "@ant-design/icons";
import { PdfReportModal } from "components/report/PdfReportModal";
import { formatDate, formatMoney, maskCNPJ } from "utils";
import { getAllInternalTransferCompanies, getInternalTransferStatement } from "services/internal-transfer-service";
import { isMasterAdmin } from "./internal-transfer-access";
import { CompanyBalance } from "./internal-transfer-company-select";
import { useCompanyBalance } from "./internal-transfer-balance";
import { InternalTransferModal, RECEIVE, SEND } from "./internal-transfer-modal";

const { Content } = Layout;
const { RangePicker } = DatePicker;

const mapStateToProps = (state) => ({
    login: state.authReducer.loginReducer
});

const STATUS_OPTIONS = [
    { value: "", label: "Todos os status" },
    { value: "C", label: "Concluída" },
    { value: "F", label: "Falhou" },
    { value: "U", label: "Em verificação" },
    { value: "P", label: "Em processamento" }
];

const statusLabel = (code) => {
    const option = STATUS_OPTIONS.find((item) => item.value === code);
    return option ? option.label : code;
};

const NEUTRAL_COLOR = "#8c8c8c";

const isCompleted = (line) => !line.status || line.status.code === "C";

const STATUS_COLORS = {
    C: "#52c41a",
    F: "#ff4d4f",
    U: "#faad14",
    P: "#8c8c8c"
};

const toPeriod = (dates) => {
    if (!dates || !dates[0] || !dates[1]) {
        return null;
    }
    const [start, end] = dates.map((date) => {
        const [day, month, year] = date.split("/");
        return `${year}-${month}-${day}`;
    });
    return { dates: `${start}:${end}`, label: `${dates[0]} a ${dates[1]}` };
};

const InternalTransferStatementScreen = (props) => {
    const { login } = props;
    const navigate = useNavigate();
    const { companyId, parentId: routeParentId } = useParams();
    const master = isMasterAdmin(login);
    const parentId = master ? routeParentId : undefined;
    const readOnly = !!parentId;
    const backPath = parentId ? `/internal-transfer/parent/${parentId}` : "/internal-transfer";

    const [data, setData] = useState([]);
    const [total, setTotal] = useState(0);
    const [company, setCompany] = useState(null);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [period, setPeriod] = useState(null);
    const [status, setStatus] = useState("");
    const [reload, setReload] = useState(0);
    const [eligibleCompanies, setEligibleCompanies] = useState([]);
    const [modal, setModal] = useState({ open: false });
    const [printShow, setPrintShow] = useState(false);
    const [printData, setPrintData] = useState(null);
    const [printing, setPrinting] = useState(false);
    const [balance, loadBalance] = useCompanyBalance();
    const loadedCompanyId = company ? company.id : null;
    const loadedCompanyEligible = !!(company && company.eligible);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const res = await getInternalTransferStatement(companyId, {
                    parent: parentId,
                    status: readOnly ? status : undefined,
                    dates: period && period.dates,
                    page,
                    size: pageSize
                });
                setData(res.data);
                setTotal(res.total);
                setCompany(res.company);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [companyId, parentId, readOnly, status, period, page, pageSize, reload]);

    useEffect(() => {
        loadBalance(loadedCompanyEligible ? loadedCompanyId : null, parentId);
    }, [loadedCompanyId, loadedCompanyEligible, parentId, reload, loadBalance]);

    useEffect(() => {
        if (readOnly) {
            return;
        }
        const load = async () => {
            setEligibleCompanies(await getAllInternalTransferCompanies());
        };
        load();
    }, [readOnly, reload]);

    const onCloseModal = (changed) => {
        setModal({ open: false });
        if (changed) {
            setReload((value) => value + 1);
        }
    };

    const print = async () => {
        setPrinting(true);
        try {
            const res = await getInternalTransferStatement(companyId, {
                parent: parentId,
                status: readOnly ? status : undefined,
                dates: period && period.dates,
                listAll: true
            });
            setPrintData({
                company: res.company || company,
                balance: balance && !balance.loading ? balance.value : null,
                period: period && period.label,
                status: readOnly && status ? statusLabel(status) : null,
                lines: res.data,
                showStatus: readOnly
            });
            setPrintShow(true);
        } finally {
            setPrinting(false);
        }
    };

    const canOperate = !readOnly && company && company.eligible;

    return (
        <Content>
            <Space direction="vertical" style={{ width: "100%" }}>
                <Button type="link" icon={<ArrowLeftOutlined />} style={{ padding: 0 }} onClick={() => navigate(backPath)}>
                    Voltar
                </Button>
                <Row justify="space-between" align="middle" gutter={[16, 16]}>
                    <Col>
                        <Typography.Title level={4} style={{ margin: 0 }}>
                            {company ? company.fantasyName : ""}
                        </Typography.Title>
                        <Typography.Text type="secondary">{company ? `CNPJ: ${company.cnpj ? maskCNPJ(company.cnpj) : ""}` : ""}</Typography.Text>
                        <div>
                            <CompanyBalance balance={balance} />
                        </div>
                    </Col>
                    <Col>
                        <Space wrap>
                            <Button icon={<PrinterOutlined />} loading={printing} onClick={print}>
                                Imprimir relatório
                            </Button>
                            {!readOnly && (
                                <Button disabled={!canOperate} onClick={() => setModal({ open: true, mode: SEND, company })}>
                                    Enviar transferência
                                </Button>
                            )}
                            {!readOnly && (
                                <Button type="primary" disabled={!canOperate} onClick={() => setModal({ open: true, mode: RECEIVE, company })}>
                                    Receber transferência
                                </Button>
                            )}
                        </Space>
                    </Col>
                </Row>
            </Space>
            <Table
                rowKey="id"
                style={{ marginTop: 16 }}
                dataSource={data}
                loading={loading}
                bordered
                scroll={{ y: "50vh", x: readOnly ? 1200 : 800 }}
                title={() => (
                    <Row justify="end" gutter={[16, 8]}>
                        {readOnly && (
                            <Col>
                                <Select
                                    style={{ width: 200 }}
                                    value={status}
                                    options={STATUS_OPTIONS}
                                    onChange={(value) => {
                                        setPage(1);
                                        setStatus(value);
                                    }}
                                />
                            </Col>
                        )}
                        <Col>
                            <RangePicker
                                format="DD/MM/YYYY"
                                allowClear
                                disabled={loading}
                                onChange={(_, dates) => {
                                    setPage(1);
                                    setPeriod(toPeriod(dates));
                                }}
                            />
                        </Col>
                    </Row>
                )}
                pagination={{
                    current: page,
                    total,
                    pageSize,
                    showTotal: (total, range) => `${range[1]} de ${total}`,
                    disabled: loading,
                    showSizeChanger: true,
                    onChange: (newPage, newPageSize) => {
                        setPage(newPageSize !== pageSize ? 1 : newPage);
                        setPageSize(newPageSize);
                    }
                }}
            >
                <Table.Column width={150} align="center" title="Data da transferência" dataIndex="createdAt" key="createdAt" render={(value) => formatDate(value)} />
                <Table.Column
                    width={170}
                    align="center"
                    title="Valor Transferido"
                    dataIndex="amount"
                    key="amount"
                    render={(value, line) => (
                        <Typography.Text style={{ color: !isCompleted(line) ? NEUTRAL_COLOR : value < 0 ? "#ff4d4f" : undefined }}>
                            {formatMoney(value)}
                        </Typography.Text>
                    )}
                />
                <Table.Column title="Origem/Destino" dataIndex="counterpartName" key="counterpartName" />
                <Table.Column
                    width={140}
                    align="center"
                    title="Tipo"
                    dataIndex="type"
                    key="type"
                    render={(value, line) => (
                        <Typography.Text style={{ color: !isCompleted(line) ? NEUTRAL_COLOR : value === "SENT" ? "#ff4d4f" : "#52c41a" }}>
                            {value === "SENT" ? "Transferido" : "Recebido"}
                        </Typography.Text>
                    )}
                />
                {readOnly && (
                    <Table.Column
                        width={160}
                        align="center"
                        title="Status"
                        dataIndex="status"
                        key="status"
                        render={(value) => value && (
                            <Typography.Text style={{ color: STATUS_COLORS[value.code] }}>{statusLabel(value.code)}</Typography.Text>
                        )}
                    />
                )}
                {readOnly && <Table.Column width={220} title="Motivo" dataIndex="errorMessage" key="errorMessage" />}
                {readOnly && <Table.Column width={100} align="center" title="Retries" dataIndex="retryCount" key="retryCount" />}
            </Table>
            <InternalTransferModal
                open={modal.open}
                mode={modal.mode}
                company={modal.company}
                companies={eligibleCompanies}
                onClose={onCloseModal}
            />
            <PdfReportModal sender={login.user.name} data={printData} pdfModel="internalTransfer" printShow={printShow} setPrintShow={setPrintShow} />
        </Content>
    );
};

export default connect(mapStateToProps, {})(InternalTransferStatementScreen);
